import { useState, useRef, useEffect, useMemo } from 'react';
import { INCIDENTS_API } from '@/lib/incidentsApi';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Play, CheckCircle, XCircle, Loader2, FileSpreadsheet, FileText, FileImage, X } from 'lucide-react';
import { usePermissions } from '@/contexts/PermissionsContext';
import { permissionDeniedTitle } from '@/lib/permissionUi';
import { useWorkflowFlags } from '@/contexts/FeatureFlagContext';
import { filterSecuritySubcategories } from '@/lib/workflowFeatureFlags';

const API = INCIDENTS_API;

/** Full Security list; LaunchDarkly enable-* flags filter gated entries at render time. */
const BASE_CATEGORY_OPTIONS = {
  'Static Report': ['OTHER'],
  'Code Report': ['ABAP', 'FIORI'],
  Security: [
    'SECURITY',
    'New User Provisioning',
    'User Termination',
    'Password Reset',
    'PAM Health Offboarding',
    'Firefighter ID',
    'Role Modification',
    'BASIS',
  ],
  'Data Migration': ['ABAP', 'OTHER'],
  Interface: ['PI/PO', 'OTHER'],
  Enhancement: ['ABAP', 'FIORI', 'OTHER'],
  Other: ['OTHER'],
};

const DEFAULT_FORM = {
  ticket_number: '',
  title: '',
  category: 'Code Report',
  sub_category: 'ABAP',
  description: '',
  model_id: '',
  attachment: '',
};

const FFID_FORM_ACCEPT = '.pdf,.doc,.docx';
const FFID_FORM_EXTS = ['.pdf', '.doc', '.docx'];
export const SU53_EVIDENCE_ACCEPT = '.png,.jpg,.jpeg,.webp,.xlsx,.xls,.csv';
export const SU53_EVIDENCE_EXTS = ['.png', '.jpg', '.jpeg', '.webp', '.xlsx', '.xls', '.csv'];
export const TRACE_EVIDENCE_ACCEPT = '.xlsx,.xls,.csv,.txt,.pdf,.png,.jpg,.jpeg,.webp';
export const TRACE_EVIDENCE_EXTS = ['.xlsx', '.xls', '.csv', '.txt', '.pdf', '.png', '.jpg', '.jpeg', '.webp'];

const EVIDENCE_CONTENT_TYPES = {
  '.png': 'image/png',
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.webp': 'image/webp',
  '.pdf': 'application/pdf',
  '.txt': 'text/plain',
  '.csv': 'text/csv',
  '.xls': 'application/vnd.ms-excel',
  '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
};

export function evidenceExtensionAllowed(filename, extensions) {
  const name = String(filename || '').toLowerCase();
  return extensions.some((ext) => name.endsWith(ext));
}

export function evidenceContentType(file) {
  const name = String(file?.name || '').toLowerCase();
  const ext = Object.keys(EVIDENCE_CONTENT_TYPES).find((suffix) => name.endsWith(suffix));
  return file?.type || (ext ? EVIDENCE_CONTENT_TYPES[ext] : '');
}

function evidenceFileIcon(filename) {
  const name = String(filename || '').toLowerCase();
  if (/\.(png|jpe?g|webp)$/.test(name)) return FileImage;
  if (/\.(xlsx|xls|csv)$/.test(name)) return FileSpreadsheet;
  return FileText;
}

export function RoleModEvidenceFields({
  su53File,
  traceFile,
  su53InputRef,
  traceInputRef,
  onSu53File,
  onTraceFile,
}) {
  const choose = (event, extensions, message, onFile) => {
    const selected = event.target.files?.[0];
    if (!selected) return;
    if (!evidenceExtensionAllowed(selected.name, extensions)) {
      toast.error(message);
      event.target.value = '';
      return;
    }
    onFile(selected);
  };
  const Su53Icon = evidenceFileIcon(su53File?.name);
  const TraceIcon = evidenceFileIcon(traceFile?.name);
  return (
    <div className="space-y-1" data-testid="role-mod-evidence-upload">
      <Label>Authorization evidence <span className="text-muted-foreground text-xs">(optional)</span></Label>
      <p className="pt-2 text-sm font-medium">SU53 evidence</p>
      <p className="text-xs text-muted-foreground">
        Attach a failed SU53 screenshot or SU53 spreadsheet/export.
      </p>
      {su53File ? (
        <div className="flex items-center gap-2 rounded-md border border-cp-ok bg-cp-inset px-3 py-2 text-sm text-cp-ok">
          <Su53Icon className="h-4 w-4 shrink-0" />
          <span className="flex-1 truncate" data-testid="su53-evidence-filename">{su53File.name}</span>
          <button type="button" onClick={() => onSu53File(null)} aria-label="Remove SU53 evidence">
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <label className="flex cursor-pointer items-center gap-2 rounded-md border border-dashed border-muted-foreground/40 bg-muted/30 px-3 py-3 text-sm text-muted-foreground hover:border-primary/50 hover:bg-muted/50 transition-colors">
          <FileSpreadsheet className="h-4 w-4 shrink-0" />
          <span>Upload SU53 evidence</span>
          <input
            ref={su53InputRef}
            type="file"
            accept={SU53_EVIDENCE_ACCEPT}
            className="sr-only"
            data-testid="su53-evidence-input"
            onChange={(event) => choose(
              event,
              SU53_EVIDENCE_EXTS,
              'Attach a PNG, JPEG, WebP, Excel, or CSV SU53 evidence file',
              onSu53File,
            )}
          />
        </label>
      )}
      <p className="pt-3 text-xs font-medium uppercase tracking-wide text-muted-foreground">OR</p>
      <div className="pt-2">
        <p className="text-sm font-medium">SAP authorization trace</p>
        <p className="text-xs text-muted-foreground">
          Attach an authorization trace export when SU53 is unavailable or insufficient.
        </p>
        {traceFile ? (
          <div className="mt-1 flex items-center gap-2 rounded-md border border-cp-ok bg-cp-inset px-3 py-2 text-sm text-cp-ok">
            <TraceIcon className="h-4 w-4 shrink-0" />
            <span className="flex-1 truncate" data-testid="trace-evidence-filename">{traceFile.name}</span>
            <button type="button" onClick={() => onTraceFile(null)} aria-label="Remove authorization trace">
              <X className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <label className="mt-1 flex cursor-pointer items-center gap-2 rounded-md border border-dashed border-muted-foreground/40 bg-muted/30 px-3 py-3 text-sm text-muted-foreground">
            <FileSpreadsheet className="h-4 w-4 shrink-0" />
            <span>Upload authorization trace</span>
            <input
              ref={traceInputRef}
              type="file"
              accept={TRACE_EVIDENCE_ACCEPT}
              className="sr-only"
              data-testid="trace-evidence-input"
              onChange={(event) => choose(
                event,
                TRACE_EVIDENCE_EXTS,
                'Attach an Excel, CSV, text, PDF, or image authorization trace',
                onTraceFile,
              )}
            />
          </label>
        )}
      </div>
      <p className="pt-3 text-xs text-muted-foreground">
        Either file can be attached. A description is enough to continue.
      </p>
    </div>
  );
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function TriggerWorkflow() {
  const { hasPermission } = usePermissions();
  const canTrigger = hasPermission('trigger-workflow', 'create');
  const workflowFlags = useWorkflowFlags();
  const categoryOptions = useMemo(() => {
    const options = { ...BASE_CATEGORY_OPTIONS };
    options.Security = filterSecuritySubcategories(options.Security, workflowFlags);
    return options;
  }, [workflowFlags]);
  const categories = useMemo(() => Object.keys(categoryOptions), [categoryOptions]);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [status, setStatus] = useState(null); // null | 'loading' | 'success' | 'error'
  const [response, setResponse] = useState(null);
  const [excelFile, setExcelFile] = useState(null); // File object
  const [ffidFormFile, setFfidFormFile] = useState(null);
  const [su53ImageFile, setSu53ImageFile] = useState(null);
  const [traceFile, setTraceFile] = useState(null);
  const fileInputRef = useRef(null);
  const ffidFileInputRef = useRef(null);
  const su53FileInputRef = useRef(null);
  const traceFileInputRef = useRef(null);

  const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));
  const setCategory = (category) => {
    setForm(prev => ({
      ...prev,
      category,
      sub_category: categoryOptions[category]?.[0] || '',
    }));
  };
  const setSubCategory = (sub_category) => {
    setForm((prev) => {
      const next = { ...prev, sub_category };
      if (sub_category === 'Firefighter ID') {
        if (!String(prev.title || '').trim()) {
          next.title = 'Firefighter ID request';
        }
        next.description = '';
      }
      return next;
    });
  };

  useEffect(() => {
    const allowed = categoryOptions[form.category] || [];
    if (form.sub_category && !allowed.includes(form.sub_category)) {
      setForm(prev => ({
        ...prev,
        sub_category: allowed[0] || '',
      }));
    }
  }, [categoryOptions, form.category, form.sub_category]);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.xlsx')) {
      toast.error('Only .xlsx files are supported');
      e.target.value = '';
      return;
    }
    setExcelFile(file);
  };

  const handleFfidFormChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const name = file.name.toLowerCase();
    if (!FFID_FORM_EXTS.some((ext) => name.endsWith(ext))) {
      toast.error('Attach a PDF or Word form (.pdf, .doc, .docx)');
      e.target.value = '';
      return;
    }
    setFfidFormFile(file);
  };

  const clearFile = () => {
    setExcelFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const clearFfidForm = () => {
    setFfidFormFile(null);
    if (ffidFileInputRef.current) ffidFileInputRef.current.value = '';
  };

  const clearSu53Image = () => {
    setSu53ImageFile(null);
    if (su53FileInputRef.current) su53FileInputRef.current.value = '';
  };

  const clearTraceFile = () => {
    setTraceFile(null);
    if (traceFileInputRef.current) traceFileInputRef.current.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canTrigger) {
      toast.error(permissionDeniedTitle(false, 'trigger workflows'));
      return;
    }
    const isFfid = form.sub_category === 'Firefighter ID';
    const isRoleModification = form.sub_category === 'Role Modification';
    if (!form.ticket_number || !form.title) {
      toast.error('Ticket number and title are required');
      return;
    }
    if (!isFfid && !form.description) {
      toast.error('Ticket number, title, and description are required');
      return;
    }
    if (isFfid && !ffidFormFile && !String(form.description || '').trim()) {
      toast.error('Attach the FFID form (PDF or Word) or paste the form fields');
      return;
    }
    setStatus('loading');
    setResponse(null);
    try {
      const payload = {
        ticket_number: form.ticket_number.trim(),
        title: form.title.trim(),
        category: form.category,
        sub_category: form.sub_category || null,
        description: form.description.trim() || (isFfid && ffidFormFile ? form.title.trim() : form.description.trim()),
        model_id: form.model_id.trim() || null,
        attachment: isRoleModification ? null : (form.attachment.trim() || null),
      };

      if (excelFile) {
        payload.excel_spec_b64 = await fileToBase64(excelFile);
      }
      if (ffidFormFile) {
        payload.ffid_form_b64 = await fileToBase64(ffidFormFile);
        payload.ffid_form_filename = ffidFormFile.name;
      }
      if (isRoleModification && su53ImageFile) {
        payload.attachment = await fileToBase64(su53ImageFile);
        payload.attachment_filename = su53ImageFile.name;
        payload.attachment_content_type = evidenceContentType(su53ImageFile);
      }
      if (isRoleModification && traceFile) {
        payload.trace_attachment = await fileToBase64(traceFile);
        payload.trace_attachment_filename = traceFile.name;
        payload.trace_attachment_content_type = evidenceContentType(traceFile) || 'text/plain';
      }

      const res = await axios.post(`${API}/integrations/servicenow/inbound`, payload);
      setStatus('success');
      setResponse(res.data);
      toast.success(`Workflow triggered for ${form.ticket_number}`);
    } catch (err) {
      setStatus('error');
      setResponse(err.response?.data || { message: err.message });
      toast.error('Failed to trigger workflow');
    }
  };

  const handleReset = () => {
    setForm(DEFAULT_FORM);
    setStatus(null);
    setResponse(null);
    clearFile();
    clearFfidForm();
    clearSu53Image();
    clearTraceFile();
  };

  return (
    <div className="p-6 max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Trigger Workflow</h1>
        <p className="text-muted-foreground mt-1">
          Post a ticket directly to the workflow engine via{' '}
          <code className="text-xs bg-muted px-1 py-0.5 rounded">
            POST /api/integrations/servicenow/inbound
          </code>
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Workflow Request</CardTitle>
          <CardDescription>Fill in the ticket details and submit to start a workflow run.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label htmlFor="ticket_number">Ticket Number <span className="text-cp-crit">*</span></Label>
                <Input
                  id="ticket_number"
                  placeholder="e.g. TEST-POSTMAN-108"
                  value={form.ticket_number}
                  onChange={e => set('ticket_number', e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="model_id">Model ID <span className="text-muted-foreground text-xs">(optional)</span></Label>
                <Input
                  id="model_id"
                  placeholder="Leave blank for default"
                  value={form.model_id}
                  onChange={e => set('model_id', e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-1">
              <Label htmlFor="title">Title <span className="text-cp-crit">*</span></Label>
              <Input
                id="title"
                placeholder="e.g. Postman test ticket"
                value={form.title}
                onChange={e => set('title', e.target.value)}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label>Category</Label>
                <Select value={form.category} onValueChange={setCategory}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(c => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Sub Category</Label>
                <Select value={form.sub_category} onValueChange={setSubCategory}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(categoryOptions[form.category] || []).map(s => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1">
              <Label htmlFor="description">
                Description{' '}
                {form.sub_category === 'Firefighter ID' ? (
                  <span className="text-muted-foreground text-xs">(optional if a form is attached)</span>
                ) : (
                  <span className="text-cp-crit">*</span>
                )}
              </Label>
              <Textarea
                id="description"
                placeholder="Describe what the agent should do..."
                rows={5}
                value={form.description}
                onChange={e => set('description', e.target.value)}
              />
            </div>

            {form.sub_category !== 'Role Modification' && (
              <div className="space-y-1">
                <Label htmlFor="attachment">Attachment <span className="text-muted-foreground text-xs">(optional)</span></Label>
                <Input
                  id="attachment"
                  placeholder="URL or text attachment"
                  value={form.attachment}
                  onChange={e => set('attachment', e.target.value)}
                />
              </div>
            )}

            {form.sub_category === 'Role Modification' && (
              <RoleModEvidenceFields
                su53File={su53ImageFile}
                traceFile={traceFile}
                su53InputRef={su53FileInputRef}
                traceInputRef={traceFileInputRef}
                onSu53File={setSu53ImageFile}
                onTraceFile={setTraceFile}
              />
            )}

            {/* FFID request form — Firefighter ID only */}
            {form.sub_category === 'Firefighter ID' && (
              <div className="space-y-1">
                <Label>FFID request form (PDF or Word)</Label>
                {ffidFormFile ? (
                  <div className="flex items-center gap-2 rounded-md border border-cp-ok bg-cp-inset px-3 py-2 text-sm text-cp-ok">
                    <FileText className="h-4 w-4 shrink-0 text-cp-ok" />
                    <span className="flex-1 truncate">{ffidFormFile.name}</span>
                    <button
                      type="button"
                      onClick={clearFfidForm}
                      className="text-cp-ok hover:text-cp-ok"
                      aria-label="Remove file"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <label className="flex cursor-pointer items-center gap-2 rounded-md border border-dashed border-muted-foreground/40 bg-muted/30 px-3 py-3 text-sm text-muted-foreground hover:border-primary/50 hover:bg-muted/50 transition-colors">
                    <FileText className="h-4 w-4 shrink-0" />
                    <span>Click to upload .pdf, .doc, or .docx</span>
                    <input
                      ref={ffidFileInputRef}
                      type="file"
                      accept={FFID_FORM_ACCEPT}
                      className="sr-only"
                      onChange={handleFfidFormChange}
                    />
                  </label>
                )}
              </div>
            )}
            {form.category === 'Code Report' && (
              <div className="space-y-1">
                <Label>
                  Functional Spec (.xlsx){' '}
                  <span className="text-muted-foreground text-xs">(optional — ABAP generation context)</span>
                </Label>
                {excelFile ? (
                  <div className="flex items-center gap-2 rounded-md border border-cp-ok bg-cp-inset px-3 py-2 text-sm text-cp-ok">
                    <FileSpreadsheet className="h-4 w-4 shrink-0 text-cp-ok" />
                    <span className="flex-1 truncate">{excelFile.name}</span>
                    <button
                      type="button"
                      onClick={clearFile}
                      className="text-cp-ok hover:text-cp-ok"
                      aria-label="Remove file"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <label className="flex cursor-pointer items-center gap-2 rounded-md border border-dashed border-muted-foreground/40 bg-muted/30 px-3 py-3 text-sm text-muted-foreground hover:border-primary/50 hover:bg-muted/50 transition-colors">
                    <FileSpreadsheet className="h-4 w-4 shrink-0" />
                    <span>Click to upload .xlsx spec</span>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".xlsx"
                      className="sr-only"
                      onChange={handleFileChange}
                    />
                  </label>
                )}
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <span title={permissionDeniedTitle(canTrigger, 'trigger workflows')}>
                <Button
                  type="submit"
                  disabled={!canTrigger || status === 'loading'}
                  className="flex items-center gap-2"
                >
                  {status === 'loading'
                    ? <Loader2 className="w-4 h-4 animate-spin" />
                    : <Play className="w-4 h-4" />}
                  {status === 'loading' ? 'Triggering...' : 'Trigger Workflow'}
                </Button>
              </span>
              <span title={permissionDeniedTitle(canTrigger, 'reset this form')}>
                <Button
                  type="button"
                  variant="outline"
                  disabled={!canTrigger}
                  onClick={handleReset}
                >
                  Reset
                </Button>
              </span>
            </div>
          </form>
        </CardContent>
      </Card>

      {status && status !== 'loading' && (
        <Card className={status === 'success' ? 'border-cp-ok' : 'border-cp-crit'}>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              {status === 'success'
                ? <CheckCircle className="w-5 h-5 text-cp-ok" />
                : <XCircle className="w-5 h-5 text-cp-crit" />}
              {status === 'success' ? 'Workflow Triggered' : 'Error'}
              <Badge variant={status === 'success' ? 'default' : 'destructive'} className="ml-auto">
                {status === 'success' ? '202 Accepted' : 'Failed'}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <pre className="text-xs bg-muted rounded p-3 overflow-auto whitespace-pre-wrap">
              {JSON.stringify(response, null, 2)}
            </pre>
            {status === 'success' && (
              <p className="text-sm text-muted-foreground mt-2">
                Workflow is running asynchronously. Open ticket{' '}
                <a href={`/tickets/${form.ticket_number}`} className="underline text-primary">
                  {form.ticket_number}
                </a>{' '}
                to track progress.
              </p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
